import { useId } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The strip above every list in the admin panel: search on the left, filters and the section's
 * primary action on the right.
 *
 * Before this, each section had a different header shape — Roles and Invitations put a sentence of
 * prose where the search field belongs and a button on the right, Members had no header at all (so
 * a roster of any size had no way to find anyone), and Usage put a segmented control there. Having
 * one strip means the primary action for a section is always in the same place, and no section is
 * missing search by accident.
 */

export interface OrgAdminToolbarProps {
  /** Placeholder AND accessible name for the search field. Omit to render no search. */
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Filters, segmented controls — rendered before the primary action. */
  filters?: React.ReactNode;
  /** The section's primary action. Exactly one per section. */
  action?: React.ReactNode;
  className?: string;
}

export function OrgAdminToolbar({
  searchPlaceholder,
  searchValue = '',
  onSearchChange,
  filters,
  action,
  className,
}: OrgAdminToolbarProps) {
  const inputId = useId();

  return (
    <div
      className={cn(
        'oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2',
        className,
      )}
    >
      {searchPlaceholder && onSearchChange ? (
        // Capped: a search box stretched across 1100px of a wide screen reads as an empty form
        // field rather than as a filter, and puts its own placeholder a long way from the results.
        <div className="relative min-w-0 flex-1 basis-52 sm:max-w-[340px]">
          <label htmlFor={inputId} className="sr-only">
            {searchPlaceholder}
          </label>
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            id={inputId}
            type="search"
            value={searchValue}
            placeholder={searchPlaceholder}
            onChange={(event) => onSearchChange(event.target.value)}
            className={cn(
              'h-8 w-full min-w-0 rounded-md border border-border/70 bg-background/60 pl-8 pr-8',
              'text-[13px] text-foreground placeholder:text-muted-foreground',
              'transition-colors focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              // Safari renders its own clear affordance for type=search on top of ours.
              '[&::-webkit-search-cancel-button]:appearance-none',
            )}
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      ) : (
        <div className="min-w-0 flex-1" />
      )}

      {(filters || action) && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {filters}
          {action}
        </div>
      )}
    </div>
  );
}

/**
 * The count line under a list. Small, but it is the difference between "these are all of them" and
 * "is this list truncated?" — and it is where a filtered subtotal belongs.
 */
export function OrgAdminResultCount({
  shown,
  total,
  noun,
  children,
}: {
  shown: number;
  total: number;
  /** Singular noun, e.g. `'member'`. Pluralised with a trailing "s". */
  noun: string;
  children?: React.ReactNode;
}) {
  const label = shown === total ? `${total} ${noun}${total === 1 ? '' : 's'}` : `${shown} of ${total} ${noun}s`;

  return (
    <div className="oa-footer-rail flex items-center justify-between gap-3 py-1.5">
      {/* `aria-live` so filtering a list tells a screen reader user how many rows survived — the
          visual feedback (the list getting shorter) is not available to them. */}
      <p className="oa-meta" aria-live="polite">
        {label}
      </p>
      {children}
    </div>
  );
}
